import { MediaActionControl, MediaMenuAction, MediaMenuActionItem } from '@lib/media/useMediaVerbs';
import { showToast } from '@lib/utils/toastmaster';
import { useLibraryFolder } from '@stores/Library/LibraryFolder';
import { trackingStore } from '@stores/Tracking';
import { Media } from '@typings/Media';
import { useCallback, useRef, useState } from 'react';
import { useMediaDeleteModal } from '../../../context/MediaDeleteModalContext';
import { useFolderDialogs } from '../../Folders/FolderDialogs';
import { MediaRenameModalRef } from '../../MediaRenameModal/MediaRenameModal';

/**
 * The half of the media action vocabulary that CANNOT leave the library page.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * MOUNTING THIS OUTSIDE `LibraryPage`'S PROVIDERS THROWS. THAT IS THE POINT.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `useLibraryFolder()` needs a `LibraryFolderProvider`, which takes ONE concrete library, and
 * `useMediaDeleteModal()` and `useFolderDialogs()` need their providers, each mounted in exactly
 * one place (`LibraryPage.tsx`). None can be hoisted app-wide, because `/search` spans many
 * libraries and `/atlas` has none in scope.
 *
 * So the actions here are simply ABSENT on those routes rather than dimmed. That's the honest
 * presentation: rename, move, retry and delete aren't "not yet available" on a search result,
 * they're operations the surface has no library context to perform. A surface that wants them
 * mounts `useMediaMenuActions` (both halves) instead, and may only do so under those providers.
 *
 * Everything that works anywhere lives in `useMediaVerbs` — see the docblock there before
 * moving anything across.
 */
export function useMediaAdminActions(
  media: Media,
  isModifiable: boolean,
  onAfterDestructiveAction?: () => void,
) {
  const folder = useLibraryFolder();
  const { openDeleteModal } = useMediaDeleteModal();
  const folderDialogs = useFolderDialogs();
  const mediaRenameModalRef = useRef<MediaRenameModalRef>(null);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);

  const actions: MediaMenuActionItem[] = [
    { id: 'rename', label: 'Rename', disabled: !isModifiable },
    { id: 'move', label: 'Move to…', disabled: !isModifiable },
    ...(media.status === 'errored'
      ? [{ id: 'retry-processing' as const, label: 'Retry processing', disabled: false }]
      : []),
    { id: 'delete', label: 'Delete', disabled: !isModifiable, danger: true },
  ];

  const executeAdminAction = useCallback(
    async (action: MediaMenuAction, control: MediaActionControl = 'media-kebab') => {
      // Fired here rather than in the composing hook so each half reports its own actions —
      // see the matching note in `useMediaVerbs.executeVerb`.
      trackingStore.mediaMenuAction({
        action,
        mediaId: media._id,
        libraryId: media.libraryId,
        control,
      });

      switch (action) {
        case 'rename':
          mediaRenameModalRef.current?.open();
          break;

        case 'move':
          folderDialogs.moveMedia([media._id], 'menu');
          break;

        case 'retry-processing': {
          trackingStore.mediaRetried({ mediaId: media._id, libraryId: media.libraryId });
          const result = await folder.retryMedia(media.libraryId, media._id);
          if ('error' in result) {
            showToast('media-retry-failed', { error: result.message });
          } else {
            folder.refreshMediaById(media._id);
          }
          break;
        }

        case 'delete':
          openDeleteModal(media, onAfterDestructiveAction);
          break;
      }
    },
    [media, folder, openDeleteModal, folderDialogs, onAfterDestructiveAction],
  );

  return {
    actions,
    executeAdminAction,
    mediaRenameModalRef,
    isRenameModalOpen,
    setIsRenameModalOpen,
  };
}

/**
 * Which ids `useMediaAdminActions` owns. Used by the composing hook to route `executeAction`
 * to the right half, so the routing can't drift from the list above.
 */
const ADMIN_ACTION_IDS = [
  'rename',
  'move',
  'retry-processing',
  'delete',
] as const satisfies readonly MediaMenuAction[];

export const isAdminAction = (id: MediaMenuAction): boolean =>
  (ADMIN_ACTION_IDS as readonly MediaMenuAction[]).includes(id);
